export type BillingOutcome = 'saved' | 'resumed_paid' | 'charge_failed';

export type PersistResult =
  | { ok: true; billingOutcome: BillingOutcome }
  | { ok: false; message: string };

/** Persist a confirmed SetupIntent's card as the organization's default, synchronously.
 *  Returns a typed result so the dialog ONLY closes on success — a failed POST, a
 *  missing SetupIntent id, or a thrown request must keep the dialog open with an
 *  error (fails closed).
 *
 *  AIT-505: the server now derives the payment method (and the billing rule —
 *  resume a paused plan, collect an open invoice, or save-only) from the
 *  SetupIntent id itself, so the client posts `{ setupIntentId }` and nothing
 *  else. The earlier `{ paymentMethodId }` extraction from `payment_method` is
 *  gone; `useSetDefaultCard` is the only remaining caller of that legacy field,
 *  posted independently of this function. */
export async function persistConfirmedCard(
  fetchWithAuth: (url: string, init?: RequestInit) => Promise<Response>,
  orgId: string,
  setupIntent: { id?: string } | undefined,
): Promise<PersistResult> {
  const setupIntentId = setupIntent?.id;
  if (!setupIntentId) return { ok: false, message: 'Could not read the saved card. Please try again.' };
  // fetchWithAuth (use-fetch) RETHROWS on network failure / exhausted auth retry.
  // Catch it here so a throw can never escape past the caller's setSubmitting(false) —
  // an uncaught throw would wedge the dialog in submitting=true with no visible error.
  let res: Response;
  try {
    res = await fetchWithAuth(`/organizations/${orgId}/billing/payment-method`, {
      method: 'POST',
      // use-fetch does NOT default Content-Type; without it NestJS skips body
      // parsing and setupIntentId arrives undefined → 400 ("could not enable it").
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ setupIntentId }),
    });
  } catch {
    return { ok: false, message: 'Saved your card but could not reach the server to enable it. Please try again.' };
  }
  if (!res.ok) return { ok: false, message: 'Saved your card but could not enable it. Please try again.' };
  const body = (await res.json().catch(() => null)) as { billingOutcome?: BillingOutcome } | null;
  return { ok: true, billingOutcome: body?.billingOutcome ?? 'saved' };
}
